import { useState, useEffect, useCallback } from "react";
import { DrawerTransportError } from "../transport";
import {
  activeTransport,
  activeTransportKind,
  setTransportKind,
  supportedKinds,
  type TransportKind,
} from "../select-transport";
import { describeCapability, UNSUPPORTED, type DrawerCapability } from "../capability";
import { refreshCapability } from "../capability-state";
import { isTicketPrintingEnabled, setTicketPrintingEnabled } from "../ticket-settings";

/**
 * Drawer setup and diagnosis (T140, FR-105).
 *
 * The one screen where a person is expected to make hardware work, so it
 * answers the three questions they will actually have — is it available, is it
 * connected, and does it open — rather than presenting a capability matrix.
 *
 * `requestPermission` MUST be triggered by a user gesture; Chrome rejects it
 * otherwise. That is why the grant lives behind a button here and can never be
 * attempted automatically on till load, however convenient that would be.
 *
 * THE TEST RELEASE IS NOT A DRAWER TRIGGER. It is a diagnostic, and it writes
 * no `per_drawer_event` — the closed trigger list (cash sale, cash refund,
 * authorized no-sale) is the security control, and a test button that could
 * open the drawer while recording nothing would be a hole straight through it.
 * It is available only from settings, which is itself permission-gated.
 *
 * THIS SCREEN USES THE MODULE'S SHARED TRANSPORT, never its own instance. A
 * Bluetooth grant lives on the transport object that made it, so a settings
 * screen that paired against a private instance would appear to succeed and
 * leave the till unable to reach the drawer at all.
 */

type TestState = "idle" | "testing" | "opened" | "failed";

const BUS_LABELS: Record<TransportKind, string> = {
  usb: "USB cable",
  bluetooth: "Bluetooth",
};

export function PeripheralSettings() {
  const [capability, setCapability] = useState<DrawerCapability>(UNSUPPORTED);
  const [kind, setKind] = useState<TransportKind>(() => activeTransportKind());
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState<TestState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [ticketsEnabled, setTicketsEnabled] = useState(() => isTicketPrintingEnabled());

  const available = supportedKinds();

  const refresh = useCallback(async () => {
    // Goes through the module's own shared refresh, not a local probe — this
    // is what makes pairing a device HERE also make it usable everywhere
    // else (the sale-triggered drawer release, the ticket queue). A local-
    // only `detectCapability()` call would leave this screen saying
    // "Connected" while every real trigger path kept reading a stale,
    // pre-pairing capability.
    setCapability(await refreshCapability());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function chooseBus(next: TransportKind) {
    setBusy(true);
    setError(null);
    setTest("idle");
    try {
      await setTransportKind(next);
      setKind(next);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function connectDevice() {
    setBusy(true);
    setError(null);
    try {
      await activeTransport.requestPermission();
      await refresh();
    } catch (cause) {
      // A cancelled picker is not an error worth shouting about — the person
      // simply changed their mind.
      if (cause instanceof DrawerTransportError) setError(cause.message);
      else if (cause instanceof Error && !/cancel|no device selected/i.test(cause.message)) {
        setError(cause.message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function testRelease() {
    setTest("testing");
    setError(null);
    try {
      await activeTransport.kick();
      setTest("opened");
    } catch (cause) {
      setTest("failed");
      setError(cause instanceof Error ? cause.message : "The drawer did not respond.");
    }
  }

  return (
    <section className="peripherals" aria-label="Cash drawer">
      <h2 className="peripherals__title">Cash drawer</h2>

      <fieldset className="peripherals__bus">
        <legend>How the printer is connected</legend>
        {(Object.keys(BUS_LABELS) as TransportKind[]).map((option) => (
          <label key={option} className="peripherals__bus-option">
            <input
              type="radio"
              name="drawer-bus"
              value={option}
              checked={kind === option}
              disabled={busy || !available[option]}
              onChange={() => void chooseBus(option)}
            />
            <span>{BUS_LABELS[option]}</span>
            {!available[option] && (
              <span className="peripherals__bus-note">not available in this browser</span>
            )}
          </label>
        ))}
      </fieldset>

      <p className="peripherals__state" data-state={stateLabel(capability)}>
        {describeCapability(capability)}
      </p>

      {kind === "bluetooth" && (
        <p className="peripherals__note">
          Only Bluetooth Low Energy printers can be used. Many cheap thermal printers use older
          Bluetooth, which no browser can reach — if this printer pairs with a phone but never
          appears here, or pairs here and will not open the drawer, that is the reason. A USB
          cable works with those.
        </p>
      )}

      {!capability.transportSupported && (
        <p className="peripherals__note">
          Cash drawers need Chrome on Android or a desktop computer. On an iPhone or iPad no
          browser can open a drawer — this is a limitation of the device, not of the app. The
          till works normally; drawer opens are recorded by hand.
        </p>
      )}

      <div className="peripherals__actions">
        {capability.transportSupported && (
          <button type="button" onClick={() => void connectDevice()} disabled={busy}>
            {capability.deviceGranted ? "Choose a different drawer" : "Connect a drawer"}
          </button>
        )}

        <button type="button" onClick={() => void refresh()} disabled={busy}>
          Check again
        </button>

        {capability.connected && (
          <button
            type="button"
            className="is-primary"
            onClick={() => void testRelease()}
            disabled={test === "testing"}
          >
            {test === "testing" ? "Opening…" : "Test the drawer"}
          </button>
        )}
      </div>

      {test === "opened" && (
        <p className="peripherals__ok" role="status">
          The drawer was told to open. If it did not move, check that the cable runs from the
          printer to the drawer.
        </p>
      )}

      {error && (
        <p className="peripherals__error" role="alert">
          {error}
        </p>
      )}

      {capability.connected && !capability.drawerStatus && (
        <p className="peripherals__note">
          This printer cannot report whether the drawer is open, so the terminal will not warn
          about a drawer left open. It is better to say nothing than to guess.
        </p>
      )}

      <label className="peripherals__toggle">
        <input
          type="checkbox"
          checked={ticketsEnabled}
          onChange={(event) => {
            setTicketPrintingEnabled(event.target.checked);
            setTicketsEnabled(event.target.checked);
          }}
        />
        <span>Print an order ticket for the barista on every sale</span>
      </label>
    </section>
  );
}

function stateLabel(capability: DrawerCapability): string {
  if (!capability.transportSupported) return "unsupported";
  if (!capability.deviceGranted) return "ungranted";
  return capability.connected ? "connected" : "disconnected";
}

export default PeripheralSettings;
